import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  addClassifierExample,
  applyClassifier,
  deleteClassifier as deleteClassifierApi,
  fetchClassifiers,
  removeClassifierExample,
} from '../api';
import Header from '../components/Header';

interface Classifier {
  id: number;
  name: string;
  created_at: string;
  stats: string;
  threshold: string;
}

interface ClassifierResult {
  headline_id: number;
  title: string;
  url: string;
  source: string;
  probability: number;
  accepted: boolean;
  crawled_at: string;
}

interface ClassifierExample {
  headline_id: number;
  title: string;
  url: string;
  source: string;
  polarity: string;
  probability: number;
  crawled_at: string;
}

export default function ClassifierApplyPage() {
  const [classifiers, setClassifiers] = useState<Classifier[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [results, setResults] = useState<ClassifierResult[] | null>(null);
  const [examples, setExamples] = useState<ClassifierExample[] | null>(null);
  const [threshold, setThreshold] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [retraining, setRetraining] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchClassifiers()
      .then((data) => setClassifiers(data.classifiers))
      .catch(() => setError('Failed to load classifiers'));
  }, []);

  const exampleIds = useMemo(
    () => new Set((examples ?? []).map((e) => e.headline_id)),
    [examples],
  );

  const handleApply = async (classifierId: number) => {
    setSelectedId(classifierId);
    setLoading(true);
    setError(null);
    setResults(null);
    try {
      const data = await applyClassifier(classifierId);
      setResults(data.results);
      setExamples(data.examples);
      setThreshold(data.threshold);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to apply classifier',
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (classifierId: number) => {
    try {
      await deleteClassifierApi(classifierId);
      setClassifiers((prev) => prev.filter((c) => c.id !== classifierId));
      if (selectedId === classifierId) {
        setSelectedId(null);
        setResults(null);
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to delete classifier',
      );
    }
  };

  const handleAddExample = async (
    headlineId: number,
    polarity: 'positive' | 'negative',
  ) => {
    if (!selectedId || retraining) return;
    setRetraining(true);
    setError(null);
    try {
      await addClassifierExample(selectedId, headlineId, polarity);
      await handleApply(selectedId);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to retrain classifier',
      );
    } finally {
      setRetraining(false);
    }
  };

  const handleRemoveExample = async (headlineId: number) => {
    if (!selectedId || retraining) return;
    setRetraining(true);
    setError(null);
    try {
      await removeClassifierExample(selectedId, headlineId);
      await handleApply(selectedId);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to retrain classifier',
      );
    } finally {
      setRetraining(false);
    }
  };

  return (
    <div className="page-filter-apply">
      <Header />
      <Link className="back-link" to="/">
        &larr; Back to headlines
      </Link>

      <h2>Classifiers</h2>
      <Link to="/classifiers/new" className="create-filter-link">
        + Create new classifier
      </Link>
      <Link to="/classifiers/auto" className="create-filter-link">
        + Auto-generate classifier
      </Link>

      {classifiers.length === 0 && <p className="empty">No classifiers yet.</p>}

      {classifiers.length > 0 && (
        <div className="filter-list">
          {classifiers.map((c) => {
            const stats = JSON.parse(c.stats) as {
              positives: number;
              negatives: number;
            };
            return (
              <div
                key={c.id}
                className={`filter-item ${selectedId === c.id ? 'active' : ''}`}
              >
                <button
                  type="button"
                  className="filter-select-btn"
                  onClick={() => handleApply(c.id)}
                >
                  {c.name}{' '}
                  <small>
                    ({stats.positives}+ / {stats.negatives}-)
                  </small>
                </button>
                <button
                  type="button"
                  className="filter-delete-btn"
                  onClick={() => handleDelete(c.id)}
                >
                  delete
                </button>
              </div>
            );
          })}
        </div>
      )}

      {error && <p className="error">{error}</p>}
      {loading && <p className="summary-loading">Applying classifier...</p>}

      {examples !== null && !loading && (
        <div className="search-results">
          <h3>Training Examples (threshold: {threshold?.toFixed(4)})</h3>
          <table>
            <thead>
              <tr>
                <th />
                <th>Label</th>
                <th>Date</th>
                <th>Prob</th>
                <th>Headline</th>
                <th>Source</th>
              </tr>
            </thead>
            <tbody>
              {examples.map((e) => {
                const aboveThreshold =
                  threshold !== null && e.probability >= threshold;
                const correct = (e.polarity === 'positive') === aboveThreshold;
                return (
                  <tr
                    key={e.headline_id}
                    className={correct ? '' : 'near-miss'}
                  >
                    <td className="col-polarity">
                      <button
                        type="button"
                        className="retrain-btn"
                        disabled={retraining}
                        onClick={() => handleRemoveExample(e.headline_id)}
                      >
                        x
                      </button>
                    </td>
                    <td
                      className={
                        e.polarity === 'positive'
                          ? 'label-positive'
                          : 'label-negative'
                      }
                    >
                      {e.polarity === 'positive' ? '+' : '-'}
                    </td>
                    <td className="col-date">
                      {new Date(`${e.crawled_at}Z`).toLocaleString()}
                    </td>
                    <td className="col-dist">{e.probability.toFixed(4)}</td>
                    <td>
                      <a href={e.url} target="_blank" rel="noreferrer">
                        {e.title}
                      </a>
                    </td>
                    <td className="col-source">{e.source}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {results !== null && !loading && (
        <div className="search-results">
          <h3>
            Results ({results.length}, threshold: {threshold?.toFixed(2)})
          </h3>
          <table>
            <thead>
              <tr>
                <th>+</th>
                <th>-</th>
                <th>#</th>
                <th>Date</th>
                <th>Prob</th>
                <th>Headline</th>
                <th>Source</th>
              </tr>
            </thead>
            <tbody>
              {results.map((r, i) => {
                const isExample = exampleIds.has(r.headline_id);
                return (
                  <tr
                    key={r.headline_id}
                    className={r.accepted ? '' : 'near-miss'}
                  >
                    <td className="col-polarity">
                      {!isExample && (
                        <button
                          type="button"
                          className="retrain-btn label-positive"
                          disabled={retraining}
                          onClick={() =>
                            handleAddExample(r.headline_id, 'positive')
                          }
                        >
                          +
                        </button>
                      )}
                    </td>
                    <td className="col-polarity">
                      {!isExample && (
                        <button
                          type="button"
                          className="retrain-btn label-negative"
                          disabled={retraining}
                          onClick={() =>
                            handleAddExample(r.headline_id, 'negative')
                          }
                        >
                          -
                        </button>
                      )}
                    </td>
                    <td className="col-rank">{i + 1}</td>
                    <td className="col-date">
                      {new Date(`${r.crawled_at}Z`).toLocaleString()}
                    </td>
                    <td className="col-dist">{r.probability.toFixed(4)}</td>
                    <td>
                      <a href={r.url} target="_blank" rel="noreferrer">
                        {r.title}
                      </a>
                    </td>
                    <td className="col-source">{r.source}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
